import http from '../../../../api/axios';
import { apiUrl } from '../../../../api/apiUrl';
import { Values } from '../../../../forms/Team/TeamForm.types';
import { handleApiErrors } from '../../../../api/apiErrorHandler';
import { TeamForm } from '../../../../forms/Team/TeamForm';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Team } from '../../../../types/Team';
import { toast } from 'react-toastify';
import { TeamMemberAssign } from '../../../../forms/TeamMemberAssign/TeamMemberAssign';
import { Loader } from '../../../../components/loader/Loader';
import { useTranslation } from '../../../../context/translationContext';
import { paths } from '../../../RouterContainer';
import { COMPANY_TABS } from '../../Company/UserCompaniesUpdatePage';
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs';
import { useLoading } from '../../../../context/loadingContext';

/**
 * Page component represents Teams edit page
 */
export const CompanyTeamsUpdatePage = () => {
  const { companyId, id: membershipId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const { startLoading, stopLoading } = useLoading();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isCurrent = window.location.pathname.includes(`current`);

  useEffect(() => {
    setBreadcrumbs([
      {
        path: `${paths.MY_COMPANIES}`,
        name: 'navigation.my-companies',
      },
      {
        path: `${paths.MY_COMPANIES}/edit/${companyId}`,
        name: 'navigation.company-setting',
      },
      {
        path: `${paths.COMPANY_TEAMS}/${companyId}/edit/${membershipId}`,
        name: 'navigation.my-teams-edit',
      },
    ]);
  }, []);

  /**
   * Back button action
   */
  const handleOnBack = () => {
    if (window.location.pathname.includes(paths.COMPANY_TEAMS)) {
      return navigate(`/${paths.MY_COMPANIES}/edit/${isCurrent ? `current` : companyId}`, {
        state: { tab: COMPANY_TABS.TEAMS },
      });
    }
    return navigate(`/${paths.MY_TEAMS}`);
  };

  /**
   * Loads team
   */
  const fetchTeam = async () => {
    if (!membershipId) return;
    await http
      .apiGet({ url: `${apiUrl.COMPANY_TEAM_MEMBER_GET}?id=${Number(membershipId)}` })
      .then(async (response) => {
        await http.apiGet({ url: `${apiUrl.TEAM_GET}?id=${response?.data.teamId}` }).then((r) => {
          if (r) setTeam(r.data);
        });
      });
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  /**
   * Handles edit action. Uses team edit API.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPut({ url: apiUrl.TEAM_UPDATE, data: values });
      if (response?.status === 200) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  if (!team) return <Loader />;
  return (
    <>
      <h1 className="text-3xl font-bold">{t('page.team.headline-edit')}</h1>
      <div className="flex">
        <div className="w-1/2 pr-10">
          <TeamForm onSubmit={handleSubmit} initialValues={team as Values} onBack={handleOnBack} />
        </div>
        <div className="w-1/2">
          <p className="text-2xl font-bold mb-5">{t('page.team-member.headline')}</p>
          <TeamMemberAssign
            membershipId={Number(membershipId)}
            teamId={team?.id}
            isReadOnly={false}
          />
        </div>
      </div>
    </>
  );
};
